import React, { useEffect, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import {
  Button,
  Card,
  Form,
  InputGroup,
  Offcanvas,
  Nav,
  Table,
  Modal,
  Spinner,
  Row,
  Col,
  Breadcrumb,
  Container
} from 'react-bootstrap';
import { FaSearch, FaTrash, FaPlus, FaTimesCircle, FaPen } from 'react-icons/fa';
import instance from "../../services/baseService";
import { toast } from 'react-hot-toast';
import Base from '../Base';
import { ROUTES } from "../../constants/department";
import { NavLink } from 'react-router-dom';
import { truncateText } from '../../utils';
import Loader from "../common/Loader";
import { extractErrorMessage } from '../../constants/common';
const ManageDepartment = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', parent: '', manager_id: '' });
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState('');
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await instance.post(ROUTES.DEPARTMENT_LIST);
      setDepartments(res.data?.data?.list || []);
      setFilteredDepartments(res.data?.data?.list || []);
    } catch (error) {
      toast.error(() => (<span><b>Failed to fetch departments</b></span>));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await instance.post(ROUTES.DEPARTMENT_USER_LIST);
      setUsers(res.data?.data?.lists || []);
    } catch (error) {
      toast.error(() => (<span><b>Failed to fetch users</b></span>));
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Department Name is required';
        } else if (value.length > 100) {
          newErrors.name = 'Department Name cannot exceed 100 characters';
        } else if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
          newErrors.name = 'Department Name can only contain letters, numbers, and spaces';
        } else {
          delete newErrors.name;
        }
        break;
      case 'manager_id':
        if (!value) {
          newErrors.manager_id = 'Manager is required';
        } else {
          delete newErrors.manager_id;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Department Name is required';
    } else if (form.name.length > 100) {
      newErrors.name = 'Department Name cannot exceed 100 characters';
    } else if (!/^[a-zA-Z0-9\s]+$/.test(form.name)) {
      newErrors.name = 'Department Name can only contain letters, numbers, and spaces';
    }

    if (!form.manager_id) {
      newErrors.manager_id = 'Manager is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
    validateField(field, value);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (editId) {
        // Update (edit mode)
        await instance.post(ROUTES.DEPARTMENT_UPDATE(editId), form);
        toast.success(() => (<span><b>Department updated successfully</b></span>));
        setShowEdit(false);
        setEditId(null);
      } else {
        // Create (new department)
        await instance.post(ROUTES.DEPARTMENT_CREATE, form);
        toast.success(() => (<span><b>Department created successfully</b></span>));
      }

      // Common cleanup
      setForm({ name: '', parent: '', manager_id: '' });
      setErrors({});
      setShowEdit(false);
      fetchDepartments();

    } catch (error) {
      if (
        error?.response?.data?.type === 'validation_error' &&
        error?.response?.data?.data
      ) {
        const validationErrors = error.response.data.data;
        const messages = Object.values(validationErrors).flat();
        messages.forEach(msg => toast.error(msg)); 
      } else {
        const errorMessage = extractErrorMessage(error);
        toast.error(errorMessage);
      }
    }
  };
  useEffect(() => {
    if (search === '') {
      setFilteredDepartments(departments);
    }
  }, [search, departments]);

  const handleSearch = async () => {
    try {
      if (search === '')
        return
      const res = await instance.post(ROUTES.DEPARTMENT_LIST, { q: search });
      setFilteredDepartments(res.data?.data?.list);
    } catch (error) {
      toast.error(() => (<span><b>Failed to search departments</b></span>));
    }
  };

  const clearSearch = () => {
    setSearch('');
    setFilteredDepartments(departments);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    try {
      await instance.post(ROUTES.DEPARTMENT_DELETE, { department_id: deleteId });
      toast.success("Department deleted successfully")
      fetchDepartments();
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    }
    setShowConfirm(false);
  };

  const openForm = async (id = null) => {
    setEditId(id); // will be null for create

    if (id) {
      // Edit mode: fetch department data
      try {
        const res = await instance.get(ROUTES.DEPARTMENT_VIEW(id));
        setForm({
          name: res.data?.data?.name || '',
          parent: res.data?.data?.parent || '',
          manager_id: res.data?.data?.manager?.user_id || '',
        });
      } catch (error) {
        toast.error(() => (<span><b>Failed to load department details</b></span>));
        return;
      }
    } else {
      // Create mode: clear form
      setForm({ name: '', parent: '', manager_id: '' });
    }

    setErrors({});
    setShowEdit(true); // open the sidebar/modal
  };


  return (
    <Base>
      <Container>
        <Row>
          <Col className="mr-auto text-center">
            <Breadcrumb>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/' }}>Dashboard</Breadcrumb.Item>
              <Breadcrumb.Item active>Departments</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <h2 className="fs-3 fw-semibold">Departments</h2>
          <Button className="d-flex align-items-center fw-semibold" onClick={() => openForm(null)}>
            <FaPlus className="me-2" />
            Create Department
          </Button>
        </div>

        <Card className='border-0'>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <Nav variant="underline" defaultActiveKey="departments">
                <Nav.Item>
                  <Nav.Link eventKey="departments" className="text-primary">Manage Departments</Nav.Link>
                </Nav.Item>
              </Nav>
              <InputGroup className="w-auto">
                <Form.Control
                  placeholder="Search Department name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className='border-2'
                />
                <Button variant="outline-secondary" onClick={handleSearch} className='border-1'>
                  <FaSearch />
                </Button>
              </InputGroup>
            </div>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <Loader />
            ) : (
              <>
                {filteredDepartments.length === 0 ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                    <span>No Departments Found</span>
                  </div>
                ) : (
                  <Table responsive className="core-ui-table">
                    <thead>
                      <tr>
                        <th className="text-left">Dept Name</th>
                        <th className="text-left">Parent</th>
                        <th className="text-left">Manager</th>
                        <th className="text-center">View Members</th>
                        <th className="text-center">Edit</th>

                        <th className="text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDepartments.map((dept) => (
                        <tr key={dept.id}>
                          <td className="text-left">{truncateText(dept.name, 20)}</td>
                          <td className="text-left">{truncateText(departments.find(d => d.id === dept.parent)?.name || '-', 20)}</td>
                          <td className="text-left">{truncateText(users.find(u => u.user_id === dept.manager?.user_id)?.name || '-', 20)}</td>

                          <td className="text-center">
                            <Link to={`/departments/${dept.id}`}>
                              <Button variant="link">
                                <FaEye />
                              </Button>
                            </Link>
                          </td>
                          <td className="text-center" onClick={() => openForm(dept.id)}>
                            <Button variant="link">
                              <FaPen />
                            </Button>
                          </td>
                          <td className="text-center" onClick={() => confirmDelete(dept.id)}>
                            <Button variant="link" className="text-danger">
                              <FaTrash />
                            </Button>
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>

            )
            }
          </Card.Body>
        </Card>
        <Offcanvas show={showEdit} onHide={() => setShowEdit(false)} placement="end">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="!text-2xl">{editId ? 'Update' : 'Create'} Department</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="!text-base">Department Name<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Department Name"
                  value={form.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  isInvalid={!!errors.name}
                  maxLength={100}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="!text-base">Parent Department (Optional)</Form.Label>
                <Form.Select
                  value={form.parent}
                  onChange={(e) => setForm({ ...form, parent: e.target.value })}
                >
                  <option value="">Select Parent Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{truncateText(d.name, 20)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="!text-base">Manager  <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={form.manager_id}
                  onChange={(e) => handleInputChange('manager_id', e.target.value)}
                  isInvalid={!!errors.manager_id}
                >
                  <option value="">Select Manager</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>{truncateText(u.name, 20)}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.manager_id}
                </Form.Control.Feedback>
              </Form.Group>
              <div className="mt-8 flex justify-end space-x-3">
                <Button variant="outline-secondary" className="no-hover fw-bold" onClick={() => setShowEdit(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} className='fw-bold'>
                  {editId ? 'Update' : 'Create'} Department
                </Button>
              </div>
            </Form>
          </Offcanvas.Body>
        </Offcanvas>
        <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered className='border-0'>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this department?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-primary no-hover" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Base>
  );
};

export default ManageDepartment;